import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type {
  DocumentServicesObject,
  AppData,
} from '@wix/document-services-types';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../../types/tpaMessage';
import { SelectedComponentData } from '../utils/handlerUtils';
import { TpaMediaDialogService } from '../../services';

export function createComponentHandlers(
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  tpaMediaDialogService: TpaMediaDialogService,
  getSelectedComponentAppData: () => Promise<SelectedComponentData>,
) {
  const getCurrentPageId = (_msg: TPAMessage, callback: TPAMessageCallback) => {
    const pageId = documentServicesAPI.pages.getCurrentPageId();

    if (!pageId) {
      throw new Error('PageId is not defined');
    }

    callback(pageId);
  };

  const openSiteMembersSettingsDialog: TPAHandler = async () => {
    platformApiMethods.editor.openSiteMembersSettingsDialog?.();
  };

  const addComponent = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const { appData } = await getSelectedComponentAppData();

    if (!appData) {
      throw new Error('appData not found for the selected component');
    }

    if (msg.data?.appDefinitionId) {
      if (appData && !appData.isWixTPA) {
        delete msg.data.appDefinitionId;
      }
    }

    const appDefinitionId =
      msg.data?.appDefinitionId || appData.appDefinitionId;
    const page = msg.data?.page || { isHidden: false };

    const widgetId =
      msg.data?.widget?.widgetId || msg.data?.widget?.tpaWidgetId;
    const widget = Object.assign(msg.data?.widget || {}, {
      widgetId,
    });

    const options = {
      ...(msg.data || {}),
      componentType: msg.data?.componentType,
      compId: msg.compId,
      appDefinitionId,
      widget,
      page,
    };

    const addedCompData = await platformApiMethods.document.tpa.add.component(
      appData,
      '',
      options,
    );

    if (!addedCompData) {
      throw new Error('Failed to add component');
    }

    if (addedCompData?.compId) {
      callback(addedCompData.compId);
    } else {
      callback(addedCompData);
    }
  };

  const isFullWidth = async (
    _msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const [selectedComp] =
      await platformApiMethods.editor.selection.getSelectedComponents();

    if (!selectedComp) {
      throw new Error('Component should be selected for isFullWidth handler');
    }

    const fullWidth = documentServicesAPI.components.is.fullWidth(selectedComp);
    callback(fullWidth);
  };

  const setFullWidth = async (
    msg: TPAMessage,
    _callback: TPAMessageCallback,
  ) => {
    const { selectedComp, appData } = await getSelectedComponentAppData();

    if (!selectedComp) {
      throw new Error('Component should be selected for setFullWidth handler');
    }

    platformApiMethods.document.components.setFullWidth(appData, '', {
      componentRef: selectedComp,
      fullWidth: msg.data?.stretch,
      margins: msg.data?.margins,
    });
  };

  const openMediaDialog = (msg: TPAMessage, callback: TPAMessageCallback) => {
    const isSuperAppsFlow =
      documentServicesAPI.tpa.app.isSuperAppByCompId(msg.compId) &&
      msg.namespace === 'SuperApps.Settings';
    tpaMediaDialogService.openMediaPanel(msg, callback, isSuperAppsFlow);
  };

  const openLinkPanel = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    platformApiMethods.editor.openLinkPanel({} as AppData, '', {
      value: msg?.data?.link,
      showLinkTargetSection: false,
      callback,
    });
  };

  return {
    addComponent,
    setFullWidth,
    isFullWidth,
    openSiteMembersSettingsDialog,
    getCurrentPageId,
    openMediaDialog,
    openLinkPanel,
    editorIsFullWidth: isFullWidth,
  };
}
